/**
 * Report readiness for the Ask Addy report picker.
 *
 * Lives outside AskAddy.tsx because the poll-termination rule below is the
 * regression risk in this area and deserves a test that doesn't have to mount
 * the whole view: with the old two-state model, a permanently failed report was
 * indistinguishable from one still generating, so the 30s poll had no terminal
 * condition and ran until the user navigated away.
 */

export type ReportStatus = 'generating' | 'ready' | 'failed';

export interface ReportPkg {
  id: string;
  name?: string | null;
  purchasedAt?: string | null;
  status: ReportStatus;
  // Set when the failure was the audience never finishing preparation — the
  // remedy lives there, and it can be triggered from the picker.
  audienceId?: string | null;
  audienceCrawlFailed: boolean;
}

// Only Insight / Audience-profile orders are Profile Reports. Enhancements are
// also "packages" but unrelated, so we match on order type (not customPackages).
export const PROFILE_ORDER_TYPES = ['insight', 'audience-profile'];

/**
 * `packageReportStatus` is authoritative. `packageReportReady` is the fallback
 * for responses that predate it: only an explicit false there means "not
 * ready" — null/undefined (older API, missing package) count as ready, which
 * is the behaviour this view has always had.
 */
export function reportStatusOf(order: any): ReportStatus {
  const status = order?.packageReportStatus;
  if (status === 'ready' || status === 'failed' || status === 'generating') {
    return status;
  }
  return order?.packageReportReady === false ? 'generating' : 'ready';
}

/** Newest first; dedup by package. `createdDate` may be epoch-ms or ISO. */
export function buildReportList(orders: any[]): ReportPkg[] {
  const seen = new Set<string>();
  const out: ReportPkg[] = [];

  for (const o of orders || []) {
    if (
      !o?.packageId ||
      !PROFILE_ORDER_TYPES.includes(o.orderType) ||
      seen.has(o.packageId)
    ) {
      continue;
    }
    seen.add(o.packageId);
    out.push({
      id: o.packageId,
      name:
        o.audienceName || o.seedFileName || o.packageName || 'Profile report',
      purchasedAt: o.createdDate,
      status: reportStatusOf(o),
      audienceId: o.audienceId ?? null,
      audienceCrawlFailed: o.audienceCrawlFailed === true,
    });
  }

  const toTime = (v?: string | null) =>
    v ? new Date(isNaN(Number(v)) ? v : Number(v)).getTime() || 0 : 0;
  out.sort((a, b) => toTime(b.purchasedAt) - toTime(a.purchasedAt));
  return out;
}

/**
 * Whether the report list still has something worth polling for. Terminal
 * states — ready AND failed — must not keep the poll alive.
 */
export function hasGeneratingReports(reports: ReportPkg[]): boolean {
  return reports.some((r) => r.status === 'generating');
}
